/**
 * Central message registry for all UI strings.
 *
 * Each feature owns its namespace; products can swap individual namespaces
 * to localise or rebrand without touching component code.
 *
 * Namespaces:
 * - common:        Shared strings (buttons, generic UI)
 * - components:    Shared component strings (ErrorBoundary, accessibility)
 * - errors:        API and system error messages
 * - validation:    Form validation error messages
 * - registration:  Registration feature
 * - login:         Login feature
 * - forgotPassword: Forgot-password feature
 * - resetPassword: Password-reset feature
 * - clientConsent: OAuth client consent page
 * - pages:         Generic pages (home, 404)
 *
 * @example
 * ```tsx
 * import { messages } from "@/shared/i18n";
 *
 * <p>{messages.common.loading}</p>
 * <p>{messages.registration.form.emailLabel}</p>
 * <p>{messages.errors.network}</p>
 * ```
 *
 * Compatible with react-i18next: each namespace can be extracted to a
 * separate JSON translation file without structural changes.
 */

import {
  common,
  components,
  errors,
  accountActivation,
  clientConsent,
  forgotPassword,
  home,
  login,
  pages,
  registration,
  resetPassword,
  validation,
  verifyEmail,
} from "@/shared/i18n/namespaces";

export const messages = {
  // Shared
  common,
  components,
  errors,
  validation,

  // Features
  registration,
  login,
  forgotPassword,
  resetPassword,
  verifyEmail,
  accountActivation,
  clientConsent,
  home,

  // Pages
  pages,
} as const;
